<template>
  <div class="domain-panel" :class="[`mode-${mode}`]">
    <div v-if="showTitle" class="panel-head">
      <div>
        <h3>{{ heading }}</h3>
        <p>{{ subheading }}</p>
      </div>
      <el-button v-if="mode === 'settings'" size="mini" icon="el-icon-refresh" :loading="listLoading" @click="refresh">
        刷新
      </el-button>
    </div>

    <el-alert v-if="loadError" :closable="false" type="warning" class="panel-alert" show-icon>
      {{ loadError }}
    </el-alert>

    <div v-if="listLoading" class="loading-box">
      <i class="el-icon-loading"></i>
      <span>正在加载域名绑定信息...</span>
    </div>

    <template v-else>
      <div class="domain-grid">
        <section v-for="domainType in domainTypes" :key="domainType" class="domain-card">
          <div class="domain-card-head">
            <div>
              <div class="domain-card-title">{{ typeLabel(domainType) }}</div>
              <div class="domain-card-desc">
                {{ domainType === 'live' ? '用于直播业务访问。' : '用于商城业务访问。' }}
              </div>
            </div>
            <span class="status-pill" :class="statusClass(domainType)">
              {{ statusLabel(domainType) }}
            </span>
          </div>

          <div class="field">
            <label :for="`${domainType}-domain-input`">{{ typeLabel(domainType) }}</label>
            <input :id="`${domainType}-domain-input`" v-model.trim="forms[domainType]" type="text"
              :placeholder="domainType === 'live' ? '例如：live.example.com' : '例如：shop.example.com'"
              @blur="normalizeFormDomain(domainType)" />
            <div class="tips">请输入完整域名，不要携带协议头或路径。</div>
            <div v-if="formErrors[domainType]" class="error">{{ formErrors[domainType] }}</div>
          </div>

          <div v-if="recordMessage(domainType)" class="result-box">
            {{ recordMessage(domainType) }}
          </div>

          <div class="inline-actions">
            <button class="primary-btn" type="button" :disabled="actionLoading[domainType].save"
              @click="saveDomainByType(domainType)">
              {{ actionLoading[domainType].save ? '保存中...' : actionSaveText(domainType) }}
            </button>
            <button class="secondary-btn" type="button" :disabled="verifyDisabled(domainType)"
              @click="verifyDomainByType(domainType)">
              {{ actionLoading[domainType].verify ? '验证中...' : '验证解析' }}
            </button>
            <button v-if="mode === 'settings'" class="danger-btn" type="button" :disabled="deleteDisabled(domainType)"
              @click="deleteDomainByType(domainType)">
              {{ actionLoading[domainType].delete ? '删除中...' : '删除绑定' }}
            </button>
          </div>

          <div v-if="showGuide(domainType)" class="dns-guide">
            <div class="guide-title-row">
              <div class="guide-title">DNS 解析指引</div>
              <span class="guide-tag">{{ recordsMap[domainType].verified ? '已验证' : '待验证' }}</span>
            </div>

            <table class="record-table">
              <thead>
                <tr>
                  <th class="col-type">类型</th>
                  <th class="col-host">主机记录</th>
                  <th class="col-value">指向值</th>
                  <th class="col-remark">说明</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in recordsMap[domainType].dnsRecords"
                  :key="`${domainType}-${record.type}-${record.host}-${record.value}`">
                  <td class="cell-type">{{ record.type }}</td>
                  <td class="cell-host">{{ record.host }}</td>
                  <td class="cell-value">
                    <div class="value-cell">
                      <span class="value-text">{{ record.value }}</span>
                      <button
                        v-copy="record.value"
                        type="button"
                        class="copy-btn"
                      >
                        复制
                      </button>
                    </div>
                  </td>
                  <td class="cell-remark">{{ record.remark || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <!-- <div v-if="mode === 'register'" class="register-actions">
        <button class="primary-btn register-btn" type="button" :disabled="savingAll" @click="saveAllDomains">
          {{ savingAll ? '保存中...' : '保存域名并获取解析指引' }}
        </button>
        <button class="secondary-btn register-btn" type="button" :disabled="verifyAllDisabled"
          @click="verifyAllDomains">
          {{ verifyingAll ? '验证中...' : '验证域名解析' }}
        </button>
      </div> -->
    </template>
  </div>
</template>

<script>
import {
  addCommercialDomain,
  deleteCommercialDomain,
  getCommercialDomainDetail,
  listCommercialDomains,
  verifyCommercialDomain,
  getAppid
} from '@/api'
import {
  DOMAIN_TYPES,
  extractDomainList,
  buildDomainRecordsMap,
  createEmptyDomainRecordsMap,
  domainStatusText,
  domainTypeLabel,
  mergeDomainRecord,
  normalizeDomainInput,
  normalizeDomainRecord,
  summarizeDomainRecords,
  validateDomainInput,
} from '@/utils/domain-settings'

function createActionLoading() {
  return DOMAIN_TYPES.reduce((acc, type) => {
    acc[type] = {
      save: false,
      verify: false,
      delete: false,
    }
    return acc
  }, {})
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default {
  name: 'DomainSettingsPanel',
  props: {
    mode: {
      type: String,
      default: 'settings',
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      listLoading: false,
      savingAll: false,
      verifyingAll: false,
      domainAppId: null,
      loadError: '',
      forms: {
        live: '',
        shop: '',
      },
      formErrors: {
        live: '',
        shop: '',
      },
      actionLoading: createActionLoading(),
      recordsMap: createEmptyDomainRecordsMap(),
    }
  },
  computed: {
    domainTypes() {
      return DOMAIN_TYPES
    },
    heading() {
      return this.mode === 'register' ? '第二步：绑定域名' : '域名设置'
    },
    subheading() {
      return this.mode === 'register'
        ? '注册完成后需要同时绑定直播域名和商城域名，并分别完成验证。'
        : '直播域名和商城域名分开维护，支持独立查看、验证和删除。'
    },
    verifyAllDisabled() {
      if (this.verifyingAll) return true
      return this.domainTypes.some(type => {
        const record = this.recordsMap[type]
        return !(record.id || record.domain || normalizeDomainInput(this.forms[type]))
      })
    },
  },
  mounted() {
    if (this.autoLoad) {
      this.refresh()
    } else {
      this.emitState()
    }
  },
  methods: {
    typeLabel(type) {
      return domainTypeLabel(type)
    },
    normalizeFormDomain(type) {
      this.forms[type] = normalizeDomainInput(this.forms[type])
    },
    statusLabel(type) {
      const record = this.recordsMap[type]
      return domainStatusText(record.status, record.verified)
    },
    statusClass(type) {
      const record = this.recordsMap[type]
      if (record.verified) return 'is-success'

      if (record.id || record.domain) return 'is-warning'
      return 'is-default'
    },
    recordMessage(type) {
      return this.formErrors[type] || this.recordsMap[type].verifyMessage
    },
    actionSaveText(type) {
      if (!this.recordsMap[type].id) return '新增绑定'
      return '保存修改'
    },
    verifyDisabled(type) {
      if (this.actionLoading[type].verify) return true
      const record = this.recordsMap[type]
      return !(record.id || record.domain || normalizeDomainInput(this.forms[type]))
    },
    deleteDisabled(type) {
      if (this.actionLoading[type].delete) return true
      return !this.recordsMap[type].id
    },
    showGuide(type) {
      const record = this.recordsMap[type]
      return Array.isArray(record.dnsRecords) && record.dnsRecords.length > 0
    },
    setRecordsMap(nextMap, options = {}) {
      const merged = createEmptyDomainRecordsMap()

      this.domainTypes.forEach(type => {
        merged[type] = mergeDomainRecord(merged[type], nextMap[type])
        if (options.syncInputs || !this.forms[type]) {
          this.forms[type] = merged[type].domain || this.forms[type]
        }
      })

      this.recordsMap = merged
      this.emitState()
    },
    setRecord(type, nextRecord, options = {}) {
      this.setRecordsMap(
        {
          ...this.recordsMap,
          [type]: mergeDomainRecord(this.recordsMap[type], nextRecord),
        },
        options
      )
    },
    emitState() {
      const summary = summarizeDomainRecords(this.recordsMap)
      this.$emit('state-change', summary)
    },
    applyListRows(rows, options = {}) {
      const nextMap = buildDomainRecordsMap(rows || [])
      this.setRecordsMap(nextMap, { syncInputs: options.syncInputs !== false })
      return nextMap
    },
    // 域名绑定接口和新增保持同一 appid 维度，否则列表可能查不到刚创建的记录。
    async ensureDomainAppId() {
      if (this.domainAppId) {
        return this.domainAppId
      }

      const { data } = await getAppid()
      const appList = Array.isArray(data) ? data : []
      const appId = appList[0] && appList[0].id ? appList[0].id : null

      if (!appId) {
        throw new Error('未获取到商户 appid')
      }

      this.domainAppId = appId
      return appId
    },
    async fetchDomainListRows() {
      const appid = await this.ensureDomainAppId()
      const { data } = await listCommercialDomains({ appid })
      return extractDomainList(data || [])
    },
    async syncRecordIdFromList(type, expectedDomain, options = {}) {
      const attempts = options.attempts || 3
      const delayMs = options.delayMs || 300

      // 卡片能渲染仅代表新增接口返回了 domain/txt_record/cname_record，
      // 不代表当前 recordsMap 一定已经拿到了可用于 verify/delete 的真实绑定 id。
      // 这里按“domain_type + domain”去列表里轮询匹配，处理新增后列表延迟可见的情况。
      for (let index = 0; index < attempts; index += 1) {
        const rows = await this.fetchDomainListRows()
        const mapped = this.applyListRows(rows, { syncInputs: true })
        const matchedRecord = mapped[type]

        if (
          matchedRecord &&
          matchedRecord.id &&
          (!expectedDomain || normalizeDomainInput(matchedRecord.domain) === expectedDomain)
        ) {
          return matchedRecord.id
        }

        if (index < attempts - 1) {
          await wait(delayMs)
        }
      }

      return ''
    },
    validateType(type) {
      this.normalizeFormDomain(type)
      this.formErrors[type] = validateDomainInput(this.forms[type], this.typeLabel(type))
      return !this.formErrors[type]
    },
    validateAllTypes() {
      return this.domainTypes.every(type => this.validateType(type))
    },
    async loadDetailForType(type, id, options = {}) {
      if (!id) return this.recordsMap[type]

      try {
        const { data } = await getCommercialDomainDetail(id)
        const detailRecord = normalizeDomainRecord(data || {}, type)
        this.setRecord(type, detailRecord, { syncInputs: options.syncInputs })
      } catch (error) {
        if (!options.silent) {
          this.formErrors[type] = error.message || `${this.typeLabel(type)}详情加载失败`
        }
      }

      return this.recordsMap[type]
    },
    async ensureRecordId(type) {
      const currentRecord = this.recordsMap[type]
      if (currentRecord.id) {
        return currentRecord.id
      }

      const currentDomain = normalizeDomainInput(this.forms[type] || currentRecord.domain)
      if (!currentDomain) {
        return ''
      }

      const recordId = await this.syncRecordIdFromList(type, currentDomain, {
        attempts: 4,
        delayMs: 400,
      })
      if (recordId) {
        return recordId
      }

      throw new Error(`${this.typeLabel(type)}已存在域名，但列表未返回可识别的绑定记录 ID`)
    },
    async refresh() {
      this.listLoading = true
      this.loadError = ''

      try {
        const rows = await this.fetchDomainListRows()
        const nextMap = this.applyListRows(rows, { syncInputs: true })

        await Promise.all(
          this.domainTypes.map(type => {
            const record = nextMap[type]
            if (!record.id) return Promise.resolve()
            return this.loadDetailForType(type, record.id, { syncInputs: true, silent: true })
          })
        )

        const summary = summarizeDomainRecords(this.recordsMap)
        this.$emit('loaded', summary)
      } catch (error) {
        this.loadError = error.message || '加载域名绑定列表失败'
      } finally {
        this.listLoading = false
      }
    },
    async upsertDomainRecord(type) {
      if (!this.validateType(type)) {
        throw new Error(this.formErrors[type])
      }

      const domain = normalizeDomainInput(this.forms[type])
      const currentRecord = this.recordsMap[type]
      const appid = await this.ensureDomainAppId()

      // 当前后端没有 update 路由，修改域名时统一走“删旧记录，再新增新记录”。
      if (currentRecord.id) {
        await deleteCommercialDomain(currentRecord.id)
        this.setRecord(type, {
          id: '',
          domain: '',
          status: '',
          verified: false,
          verifyMessage: '',
        })
      }
      const { data } = await addCommercialDomain({
        domain,
        domain_type: type,
        appid,
      })

      const addRecord = normalizeDomainRecord(data || {}, type)
      const mergedRecord = mergeDomainRecord(currentRecord, {
        ...addRecord,
        domain: addRecord.domain || domain,
        domainType: type,
      })

      this.setRecord(type, mergedRecord, { syncInputs: true })

      if (!this.recordsMap[type].id) {
        const recordId = await this.syncRecordIdFromList(type, domain, {
          attempts: 4,
          delayMs: 400,
        })
        if (recordId) {
          this.setRecord(type, { id: recordId }, { syncInputs: true })
        }
      }

      if (this.recordsMap[type].id) {
        await this.loadDetailForType(type, this.recordsMap[type].id, { syncInputs: true, silent: true })
      }

      return this.recordsMap[type]
    },
    async saveDomainByType(type) {
      this.actionLoading[type].save = true
      this.formErrors[type] = ''

      try {
        await this.upsertDomainRecord(type)
        this.$message.success(`${this.typeLabel(type)}保存成功`)
        this.$emit('bound', summarizeDomainRecords(this.recordsMap))
      } catch (error) {
        this.formErrors[type] = error.message || `${this.typeLabel(type)}保存失败`
      } finally {
        this.actionLoading[type].save = false
      }
    },
    async saveAllDomains() {
      if (!this.validateAllTypes()) return

      this.savingAll = true
      this.loadError = ''

      const failed = []

      try {
        for (const type of this.domainTypes) {
          this.formErrors[type] = ''
          try {
            await this.upsertDomainRecord(type)
          } catch (error) {
            const message = error.message || `${this.typeLabel(type)}保存失败`
            this.formErrors[type] = message
            failed.push(message)
          }
        }

        const summary = summarizeDomainRecords(this.recordsMap)
        this.$emit('bound', summary)

        if (failed.length === 0) {
          this.$message.success('直播域名和商城域名均已保存成功')
        }
      } finally {
        this.savingAll = false
      }
    },
    async verifyDomainByType(type) {
      this.actionLoading[type].verify = true
      this.formErrors[type] = ''

      try {
        const recordId = await this.ensureRecordId(type)
        if (!recordId) {
          this.formErrors[type] = `${this.typeLabel(type)}尚未创建绑定记录`
          return
        }

        const { data } = await verifyCommercialDomain(recordId)
        if (data && typeof data === 'object') {
          const verifyRecord = normalizeDomainRecord({
            ...data,
            id: recordId,
            domain_type: type,
          }, type)
          this.setRecord(type, verifyRecord, { syncInputs: true })
        }
        await this.loadDetailForType(type, recordId, { syncInputs: true, silent: true })

        if (this.recordsMap[type].verified) {
          this.$message.success(`${this.typeLabel(type)}验证成功`)
        } else {
          this.$message.warning(this.recordsMap[type].verifyMessage || `${this.typeLabel(type)}尚未生效`)
        }

        this.$emit('verified', summarizeDomainRecords(this.recordsMap))
      } catch (error) {
        this.formErrors[type] = error.message || `${this.typeLabel(type)}验证失败`
      } finally {
        this.actionLoading[type].verify = false
      }
    },
    async verifyAllDomains() {
      if (this.verifyAllDisabled) return

      this.verifyingAll = true

      try {
        for (const type of this.domainTypes) {
          this.formErrors[type] = ''
          const recordId = await this.ensureRecordId(type)
          if (!recordId) {
            this.formErrors[type] = `${this.typeLabel(type)}尚未创建绑定记录`
            continue
          }

          try {
            const { data } = await verifyCommercialDomain(recordId)
            if (data && typeof data === 'object') {
              const verifyRecord = normalizeDomainRecord({
                ...data,
                id: recordId,
                domain_type: type,
              }, type)
              this.setRecord(type, verifyRecord, { syncInputs: true })
            }
            await this.loadDetailForType(type, recordId, { syncInputs: true, silent: true })
          } catch (error) {
            this.formErrors[type] = error.message || `${this.typeLabel(type)}验证失败`
          }
        }

        const summary = summarizeDomainRecords(this.recordsMap)
        if (summary.allVerified) {
          this.$message.success('直播域名和商城域名均已验证成功')
        }
        this.$emit('verified', summary)
      } finally {
        this.verifyingAll = false
      }
    },
    async deleteDomainByType(type) {
      const record = this.recordsMap[type]
      if (!record.id) return

      try {
        await this.$confirm(`确认删除${this.typeLabel(type)}绑定吗？`, '提示', {
          type: 'warning',
        })
      } catch (_) {
        return
      }

      this.actionLoading[type].delete = true
      this.formErrors[type] = ''

      try {
        await deleteCommercialDomain(record.id)
        this.forms[type] = ''
        this.setRecord(type, {
          id: '',
          domain: '',
          status: '',
          verified: false,
          verifyMessage: '',
        }, { syncInputs: true })
        this.$message.success(`${this.typeLabel(type)}已删除`)
        this.$emit('bound', summarizeDomainRecords(this.recordsMap))
      } catch (error) {
        this.formErrors[type] = error.message || `${this.typeLabel(type)}删除失败`
      } finally {
        this.actionLoading[type].delete = false
      }
    },
  },
}
</script>

<style scoped>
.domain-panel {
  width: 100%;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.panel-head h3 {
  margin: 0 0 6px;
  font-size: 20px;
  color: #1f2937;
}

.panel-head p {
  margin: 0;
  color: #6b7280;
  line-height: 1.6;
}

.panel-alert {
  margin-bottom: 16px;
}

.loading-box {
  min-height: 84px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4b5563;
}

.domain-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.domain-card {
  padding: 18px;
  border-radius: 18px;
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.domain-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.domain-card-title {
  margin-bottom: 6px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.domain-card-desc {
  color: #6b7280;
  line-height: 1.6;
  font-size: 13px;
}

.field {
  margin-bottom: 14px;
}

.field label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.field input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d5deea;
  border-radius: 12px;
  font-size: 15px;
  outline: none;
  transition: all 0.2s ease;
}

.field input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.08);
}

.tips {
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
}

.error {
  margin-top: 8px;
  color: #e11d48;
  font-size: 13px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-pill.is-success {
  background: #dcfce7;
  color: #15803d;
}

.status-pill.is-warning {
  background: #fef3c7;
  color: #b45309;
}

.status-pill.is-danger {
  background: #fee2e2;
  color: #b91c1c;
}

.status-pill.is-default {
  background: #e5e7eb;
  color: #6b7280;
}

.result-box {
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  color: #475569;
  line-height: 1.7;
  min-height: 46px;
}

.inline-actions,
.register-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.register-actions {
  margin-top: 20px;
}

.primary-btn,
.secondary-btn,
.danger-btn {
  border: 0;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.primary-btn {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.18);
}

.secondary-btn {
  background: #eef4ff;
  color: #2563eb;
  border: 1px solid #c7d2fe;
}

.danger-btn {
  background: #fff1f2;
  color: #e11d48;
  border: 1px solid #fecdd3;
}

.primary-btn[disabled],
.secondary-btn[disabled],
.danger-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.register-btn {
  flex: 1;
}

.dns-guide {
  margin-top: 16px;
}

.guide-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.guide-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.guide-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
}

.record-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  table-layout: fixed;
}

.record-table th,
.record-table td {
  padding: 12px 13px;
  border-bottom: 1px solid #eef2f7;
  text-align: left;
  font-size: 13px;
  vertical-align: top;
}

.record-table th {
  background: #f8fafc;
  color: #475569;
}

.col-type {
  width: 90px;
}

.col-host {
  width: 110px;
}

.col-remark {
  width: 72px;
}

.cell-type,
.cell-host,
.cell-remark {
  white-space: normal;
  word-break: break-word;
}

.cell-value {
  white-space: normal;
}

.value-cell {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.value-text {
  flex: 1;
  min-width: 0;
  line-height: 1.7;
  white-space: normal;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.copy-btn {
  flex: 0 0 auto;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid #c7d2fe;
  background: #eef4ff;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
}

.record-table tr:last-child td {
  border-bottom: 0;
}

@media (max-width: 900px) {
  .domain-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {

  .panel-head,
  .domain-card-head,
  .guide-title-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .inline-actions,
  .register-actions {
    flex-direction: column;
  }

  .register-btn {
    width: 100%;
  }

  .record-table {
    display: block;
    overflow-x: auto;
    white-space: normal;
  }

  .record-table table,
  .record-table th,
  .record-table td {
    white-space: normal;
  }

  .value-cell {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
